import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import Text from "../text/Text";
import Colors from "../../styles/Colors";

interface Option {
    label: string,
    value: string | number | boolean,
}

interface Props {
    options: Array<Option>,
    onChange: Function,
    defaultValue?: number,
    label?: string,
    isImportant?: boolean,
}

const SelectInput = (
    {
        options = [],
        onChange = () => null,
        defaultValue = 0,
        label,
        isImportant = false,
    }: Props) => {
    const COMPONENT_ID = `select-${Math.floor(Math.random() * 10000)}-${Math.floor(Math.random() * 10000)}-${Math.floor(Math.random() * 10000)}`

    const optionsContainerRef = useRef<HTMLDivElement>(null)
    const iconRef = useRef<HTMLElement>(null)

    const [selectValue, setSelectedValue] = useState(defaultValue)

    const handleSelectClick = () => {
        const optionsContainer = optionsContainerRef.current
        const icon = iconRef.current

        if (optionsContainer && icon) {
            optionsContainer.classList.toggle("is-open")
            icon.classList.toggle("is-open")
        }
    }

    const handleOnChange = (value: number) => {
        setSelectedValue(value)
        handleSelectClick()
        onChange(options[value].value)
    }

    const handleWindowClick = (e: MouseEvent) => {
        const optionsContainer = optionsContainerRef.current
        const icon = iconRef.current

        // @ts-ignore
        const {id} = e.target.dataset

        if (optionsContainer && icon) {
            if (id !== COMPONENT_ID) {
                optionsContainer.classList.remove("is-open")
                icon.classList.remove("is-open")
            }
        }
    }

    useEffect(() => {
        window.addEventListener("click", handleWindowClick)

        return () => {
            window.removeEventListener("click", handleWindowClick)
        }
    })

    return <Container data-id={COMPONENT_ID}>
        {
            label && <LabelContainer>
                <Text>{label}</Text>
                {isImportant && <Text color={Colors.green}>*</Text>}
            </LabelContainer>
        }

        <SelectContainer
            onClick={handleSelectClick}
            data-id={COMPONENT_ID}
        >
            <Icon
                ref={iconRef}
                className="fas fa-chevron-down"
                data-id={COMPONENT_ID}
            />
            <Text data-id={COMPONENT_ID}>{options[selectValue].label}</Text>
        </SelectContainer>

        <OptionsContainer
            data-id={COMPONENT_ID}
            ref={optionsContainerRef}
        >
            {
                options.map((option, index) => (
                    <OptionContainer
                        key={index}
                        onClick={() => handleOnChange(index)}
                        data-id={COMPONENT_ID}
                    >
                        <Text data-id={COMPONENT_ID}>{option.label}</Text>
                    </OptionContainer>
                ))
            }
        </OptionsContainer>
    </Container>
}

export default SelectInput

const Container = styled.div`
  width: 100%;
  position: relative;
`

const LabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 5px;
  width: 100%;
  overflow: hidden;

  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-align: start;
  }
`

const SelectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2.5px 10px;
  border: 1px solid ${Colors.darkGrey + "99"};
  border-radius: 2px;
  overflow: hidden;
  width: 100%;
  
  &:hover {
    cursor: pointer;
  }

  p {
    text-overflow: clip ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-align: start;
  }
  
  @media (max-width: 768px) {
      padding: 7.5px 10px;
  }
`

const Icon = styled.i`
  margin-right: 10px;
  color: ${Colors.darkGrey};
  transform: rotate(0deg);
  animation: linear;
  transition-duration: 200ms;
  
  &.is-open {
      transform: rotate(180deg);
  }
`

const OptionsContainer = styled.div`
  position: absolute;
  width: 100%;
  transform: scaleY(0);
  transform-origin: 50% 0;
  animation: linear;
  transition-duration: 200ms;
  z-index: 1;
  background-color: ${Colors.white};
  box-shadow: ${Colors.dropdownShadow};
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;

  &.is-open {
    transform: scaleY(1);
  }
  
  p {
    text-align: start;
  }
`

const OptionContainer = styled.div`
  background-color: ${Colors.white};
  transition-duration: 200ms;
  padding: 4px 10px;
  border-bottom: 1px solid ${Colors.darkGrey + "1A"};
  
  &:hover {
    cursor: pointer;
  }
  
  &:hover p {
    color: ${Colors.green};
  }
  
  :last-child {
    border-bottom: none;
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  
  @media (max-width: 768px) {
      padding: 7.5px 10px;
  }
`
